<template>
  <div class="d-flex flex-column gap-4">
    <!-- Action Panel with Search, Export, Sort -->
    <ActionPanel
      show-export
      show-search
      :search-v-model="filterParams.searchQuery"
      aria-label-for-sort-icon-button="Toggle filter's visibility"
      aria-label-for-export-icon-button="Export retained appointments"
      @on-search="handleSearch"
      :tabs="activeTableTabs"
      @on-sort="toggleAdvancedFiltersPanel"
      @on-export="$emit('export-requested')"
    >
      <template #right-last-content>
        <!-- Column Selection Dropdown -->
        <Dropdown
          auto-close="outside"
          @update:model-value="handleColumnOptionsChange"
          :items="tableDropdowns.column"
          :check-list-mode="true"
          :use-checked-key="true"
          class="left-box-separator"
        >
          <template #toggler>
            <div class="d-flex align-items-center">
              <IconButton
                class="static"
                icon="ri-table-line"
                aria-label="Toggle columns"
              />
              <span>Columns</span>
            </div>
          </template>
        </Dropdown>

        <!-- Table View Dropdown -->
        <Dropdown
          :model-value="isTabular"
          reduce
          :items="tableDropdowns.tableView"
          :preselected="tableDropdowns.tableView[0]"
          class="ms-2 me-2 left-box-separator"
          @update:model-value="handleViewChange"
        >
          <template #toggler>
            <div class="d-flex align-items-center">
              <IconButton
                class="static"
                icon="ri-layout-row-line"
                aria-label="Toggle filter view"
              />
              <span>View</span>
            </div>
          </template>
        </Dropdown>
      </template>
    </ActionPanel>

    <!-- Advanced Filters Panel -->
    <div
      v-if="state.isAdFiltersPanelVisible"
      class="p-2 d-flex flex-column gap-4"
    >
      <div class="retained-advanced-filters-row">
        <CustomSelect
          :model-value="filterParams.retainType"
          class="ps__child--consume"
          :options="retainTypeOptions"
          placeholder="All Types"
          :clearable="false"
          :searchable="false"
          @update:model-value="handleRetainTypeChange"
          :close-on-select="true"
        ></CustomSelect>
        <CustomSelect
          v-if="hasSelectableGradeYears"
          :model-value="filterParams.gradeYears"
          class="ps__child--consume"
          :options="gradeYears"
          placeholder="Select year"
          :clearable="false"
          :searchable="false"
          @update:model-value="handleGradeYearsChange"
          :close-on-select="true"
        ></CustomSelect>
        <CustomSelectDatePicker
          :date-ranges="optionalDateRanges"
          class="retained-datatable-container-date-picker"
          :model-value="filterParams.dates"
          :selected-school-year-limit="filterParams.gradeYears"
          :should-persist-value="true"
          @update:model-value="handleDatesChange"
        ></CustomSelectDatePicker>
      </div>
      <div class="text-end">
        <BaseButton rounded class="me-1" @click="handleClearFilter">
          Clear All
        </BaseButton>
        <BaseButton rounded solid @click="handleAdvancedFilter">
          Submit
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import Dropdown from "@/v3components/shared/Buttons/Dropdown.vue"
import IconButton from "@/v3components/shared/Buttons/IconButton.vue"
import ActionPanel from "@/v3components/shared/DataTable/ActionPanel.vue"
import CustomSelect from "@/v3components/shared/Form/CustomSelect.vue"
import CustomSelectDatePicker from "@/v3components/shared/Form/CustomSelectDatePicker.vue"

import { reactive, computed } from "vue"
import { useStore } from "vuex"

export default {
  name: "RetainedTableFilter",
  components: {
    ActionPanel,
    Dropdown,
    IconButton,
    CustomSelect,
    BaseButton,
    CustomSelectDatePicker
  },
  props: {
    gradeYears: {
      type: Array,
      default: () => []
    },
    optionalDateRanges: {
      type: Array,
      required: true
    },
    activeTableTabs: {
      type: Array,
      required: true
    },
    tableDropdowns: {
      type: Object,
      required: true
    },
    isTabular: {
      type: Object,
      default: null
    }
  },
  emits: [
    "search-updated",
    "filter-changed",
    "export-requested",
    "advance-filter-panel-toggled",
    "column-options-changed",
    "view-changed",
    "clear-filter",
    "advanced-filter",
    "retain-type-changed",
    "grade-years-changed",
    "dates-changed"
  ],
  setup(props, { emit }) {
    const store = useStore()

    // Schools retaining only the current year have nothing to choose between
    const hasSelectableGradeYears = computed(
      () => (props.gradeYears?.length || 0) > 1
    )

    const filterParams = computed(() => ({
      searchQuery: store.state.retained.search_query,
      retainType: store.state.retained.retain_type,
      gradeYears: store.state.retained.grade_years,
      dates: store.state.retained.betweenDates.join(" - ")
    }))

    const state = reactive({
      isAdFiltersPanelVisible: true,
      searchTimeOut: null
    })

    const retainTypeOptions = [
      { label: "All Types", value: null },
      { label: "Missed Request", value: "type_missed" },
      { label: "Canceled", value: "type_canceled" }
    ]

    const toggleAdvancedFiltersPanel = () => {
      state.isAdFiltersPanelVisible = !state.isAdFiltersPanelVisible
      emit("advance-filter-panel-toggled")
    }

    const handleColumnOptionsChange = (selectedColumns) => {
      emit("column-options-changed", selectedColumns)
    }

    const handleViewChange = (value) => {
      emit("view-changed", value)
    }

    const handleSearch = (value) => {
      clearInterval(state.searchTimeOut)
      state.searchTimeOut = setTimeout(() => {
        emit("search-updated", value)
      }, 500)
    }

    const handleRetainTypeChange = (value) => {
      emit("retain-type-changed", value)
    }

    const handleGradeYearsChange = (value) => {
      emit("grade-years-changed", value)
    }

    const handleDatesChange = (value) => {
      emit("dates-changed", value)
    }

    const handleClearFilter = () => {
      emit("clear-filter")
    }

    const handleAdvancedFilter = () => {
      emit("advanced-filter")
    }

    return {
      state,
      filterParams,
      hasSelectableGradeYears,
      retainTypeOptions,
      toggleAdvancedFiltersPanel,
      handleColumnOptionsChange,
      handleViewChange,
      handleSearch,
      handleRetainTypeChange,
      handleGradeYearsChange,
      handleDatesChange,
      handleClearFilter,
      handleAdvancedFilter
    }
  }
}
</script>
